import React, { useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import api from "@/lib/api";
import { LayoutDashboard, CalendarDays, Users, UserCog, Scissors, ListChecks, CreditCard, Settings as SettingsIcon, LogOut, Menu, X, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function AppShell() {
    const { t, setLang } = useI18n();
    const { user, salon, logout, refreshMe } = useAuth();
    const nav = useNavigate();
    const [open, setOpen] = useState(false);

    const sub = salon?.subscription || {};
    const showTrial = sub.status === "trial";
    const expired = sub.status === "expired";

    const links = [
        { to: "/app", icon: LayoutDashboard, label: t("dashboard.title"), end: true },
        { to: "/app/calendar", icon: CalendarDays, label: t("calendar.title") },
        { to: "/app/customers", icon: Users, label: t("customers.title") },
        { to: "/app/employees", icon: UserCog, label: t("employees.title"), ownerOnly: true },
        { to: "/app/services", icon: Scissors, label: t("services.title") },
        { to: "/app/waiting-list", icon: ListChecks, label: t("features_list.waiting_list") },
        { to: "/app/billing", icon: CreditCard, label: t("billing.title"), ownerOnly: true },
        { to: "/app/settings", icon: SettingsIcon, label: t("settings.title"), ownerOnly: true },
    ].filter((l) => !l.ownerOnly || user?.role === "owner");

    const SidebarContent = () => (
        <div className="flex h-full flex-col">
            <div className="px-6 py-5"><Logo to="/app" /></div>
            <nav className="flex-1 space-y-1 px-3">
                {links.map((l) => (
                    <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)}
                        data-testid={`sidebar-${l.end ? "dashboard" : l.to.split("/").pop()}`}
                        className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
                        <l.icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} />{l.label}
                    </NavLink>
                ))}
            </nav>
            <div className="border-t border-border/60 p-4">
                <div className="mb-3 px-2">
                    <p className="truncate text-sm font-semibold">{salon?.name}</p>
                    <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <Button variant="ghost" onClick={() => { logout(); nav("/"); }} data-testid="logout-btn" className="w-full justify-start rounded-xl text-muted-foreground">
                    <LogOut className="mr-3 h-4 w-4" />{t("common.logout")}
                </Button>
            </div>
        </div>
    );

    return (
        <div className="flex min-h-screen bg-secondary/20">
            <aside className="hidden w-64 shrink-0 border-r border-border/60 bg-card lg:block"><SidebarContent /></aside>
            {open && (
                <div className="fixed inset-0 z-50 lg:hidden">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
                    <aside className="absolute left-0 top-0 h-full w-64 bg-card"><SidebarContent /></aside>
                </div>
            )}
            <div className="flex min-w-0 flex-1 flex-col">
                <header className="sticky top-0 z-40 flex items-center justify-between border-b border-border/60 bg-background/80 px-4 py-3 backdrop-blur-xl lg:px-8">
                    <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="app-menu-btn"><Menu /></button>
                    <div className="hidden lg:block" />
                    <div className="flex items-center gap-3">
                        <LanguageSwitcher compact onChange={async (code) => { try { await api.patch("/auth/language", { language: code }); } catch {} }} />
                    </div>
                </header>

                {(showTrial || expired) && (
                    <div className={`flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 text-sm lg:px-8 ${expired ? "bg-destructive text-destructive-foreground" : "bg-accent/15 text-foreground"}`} data-testid="trial-banner">
                        <span className="flex items-center gap-2">
                            {expired ? <AlertTriangle className="h-4 w-4" /> : null}
                            {expired
                                ? t("billing.expired") + " — " + t("billing.choosePlan")
                                : `${t("billing.trial")}: ${sub.days_remaining} ${t("billing.daysRemaining")}`}
                        </span>
                        {user?.role === "owner" && (
                            <Link to="/app/billing"><Button size="sm" className="h-7 rounded-full">{t("billing.choosePlan")}</Button></Link>
                        )}
                    </div>
                )}

                <main className="flex-1 px-4 py-6 lg:px-8 lg:py-8"><Outlet /></main>
            </div>
        </div>
    );
}
